import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  alpha,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  School,
  Business,
  PersonSearch,
  ArrowForward,
} from '@mui/icons-material';
import { publicNavigation } from '@/navigation/config';

const surveys = [
  {
    title: "Students & Recent Graduates",
    description: "Share your volunteer experience and help shape the future of student volunteering. Your insights will directly influence how we connect students with meaningful opportunities.",
    icon: PersonSearch,
    color: "#FF6B6B",
    hoverColor: "#FF5252",
    path: publicNavigation[0].children?.[0].path || '',
    buttonText: "Take Student Survey",
    highlight: "5 minutes • Anonymous • Make an Impact",
  },
  {
    title: "Educators & Administrators",
    description: "Help us understand how we can better support schools in managing student volunteer programs and tracking community service hours.",
    icon: School,
    color: "#4ECDC4",
    hoverColor: "#45B8B0",
    path: publicNavigation[0].children?.[1].path || '',
    buttonText: "Take Educator Survey",
    highlight: "7 minutes • Shape Education • Drive Change",
  },
  {
    title: "Organizations & Employers",
    description: "Tell us about your volunteer program needs and help create better connections between organizations and student volunteers.",
    icon: Business,
    color: "#F7931E",
    hoverColor: "#E67E00",
    path: publicNavigation[0].children?.[2].path || '',
    buttonText: "Take Employer Survey",
    highlight: "6 minutes • Improve Recruitment • Build Community",
  },
];

const SurveyCTASection = () => {

  return (
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            component="span"
            sx={{
              color: 'white',
              bgcolor: alpha('#fff', 0.1),
              px: 3,
              py: 1,
              borderRadius: 50,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              mb: 3,
              display: 'inline-block',
              backdropFilter: 'blur(10px)',
            }}
          >
            🎯 Your Voice Matters
          </Typography>
          
          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontWeight: 800,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              mb: 2,
              mt: 2,
            }}
          >
            Help Us Build the Future of Student Volunteering
          </Typography>
          
          <Typography
            variant="h5"
            sx={{
              color: alpha('#fff', 0.9),
              maxWidth: '800px',
              mx: 'auto',
              mb: 0,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              lineHeight: 1.6,
            }}
          >
            Take a quick survey and be part of shaping how students connect with meaningful volunteer opportunities. Your feedback will directly influence our platform's development.
          </Typography>
        </motion.div>
      </Box>

      <Grid 
        container 
        spacing={4} 
        sx={{ 
          alignItems: 'stretch',
          '& .MuiGrid-item': {
            display: 'flex',
          },
        }}
      >
        {surveys.map((survey, index) => (
          <Grid item xs={12} md={4} key={survey.title}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              style={{ width: '100%' }}
            >
              <Card
                sx={{
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  background: alpha('#fff', 0.05),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha('#fff', 0.1)}`,
                  transition: 'all 0.3s ease',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    background: alpha('#fff', 0.08),
                    '& .survey-icon': {
                      transform: 'scale(1.1) rotate(5deg)',
                    },
                  },
                }}
              >
                <Box
                  className="survey-icon"
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(survey.color, 0.2),
                    color: survey.color,
                    mb: 3,
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <survey.icon sx={{ fontSize: 32 }} />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  {survey.title}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: alpha('#fff', 0.9),
                    mb: 3,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: 1.6,
                    flex: '1 1 auto',
                  }}
                >
                  {survey.description}
                </Typography>

                <Box sx={{ mt: 'auto' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: survey.color,
                      display: 'block',
                      mb: 3,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {survey.highlight}
                  </Typography>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForward />}
                    href={survey.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: '100%',
                      bgcolor: survey.color,
                      color: 'white',
                      fontWeight: 600,
                      py: 1.5,
                      boxShadow: `0 4px 14px ${alpha(survey.color, 0.4)}`,
                      '&:hover': {
                        bgcolor: survey.hoverColor,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 20px ${alpha(survey.color, 0.6)}`,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {survey.buttonText}
                  </Button>
                </Box>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default SurveyCTASection; 